import React, { useEffect, useMemo, useRef, useState } from "react";
import useClockChangeLogs, {
  FieldChangeFilter,
  FieldChangeRow,
} from "../hooks/useClockChangeLogs";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSTextField from "../../system-design/DSTextField";
import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSwitch from "../../system-design/DSSwitch";
import DSTable from "../../system-design/Table/DSTable";
import DSTableHead from "../../system-design/Table/DSTableHead";
import DSTableBody from "../../system-design/Table/DSTableBody";
import DSTableRow from "../../system-design/Table/DSTableRow";
import DSTableCell from "../../system-design/Table/DSTableCell";
import DSTableContainer from "../../system-design/Table/DSTableContainer";
import DSAlert from "../../system-design/DSAlert";
import DSChip from "../../system-design/DSChip";
import { IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KlydosPickDialog from "../../Gifts/GiftCategories/components/KlydosPickDialog";
import {
  changeLogCellSx as tableCellStyles,
  changeLogHeaderCellSx as headerCellStyles,
  changeLogChipColors,
  getDefaultChangeLogFromDate,
  parseRecordLimit,
  KlydoPreviewDialog,
} from "./changeLogShared";

interface KlydoChangeLogDialogProps {
  open: boolean;
  onClose: () => void;
  clockId: string;
  clockIdf: string;
}

// Every changelog field a klydo id can live in, with a field-agnostic id
// extractor. Adding a new klydo-bearing field later (e.g. a collection log) is
// a single entry here — keeps the presence logic generic.
type PresenceField = {
  key: keyof FieldChangeFilter;
  label: string;
  extractIds: (row: FieldChangeRow) => string[];
};

type MatchByField = { field: PresenceField; matched: string[] };
type PresenceRow = { row: FieldChangeRow; matchesByField: MatchByField[] };

const PRESENCE_FIELDS: PresenceField[] = [
  { key: "feeds", label: "Feeds", extractIds: (r) => r.feeds ?? [] },
  {
    key: "favorites",
    label: "Favorites",
    extractIds: (r) => r.favorites ?? [],
  },
  {
    key: "pendingKlydos",
    label: "Pending",
    extractIds: (r) => (r.pendingKlydos ?? []).map((p) => p.id),
  },
  {
    key: "hiddenKlydos",
    label: "Hidden",
    extractIds: (r) => (r.hiddenKlydos ?? []).map((h) => h.id),
  },
  // "Displayed" (the currently shown klydo) rotates constantly, so it is opt-in
  // via the includeDisplayed toggle to avoid flooding the table.
  {
    key: "klydo",
    label: "Displayed",
    extractIds: (r) => (r.klydo ? [r.klydo] : []),
  },
];

const KlydoChangeLogDialog: React.FC<KlydoChangeLogDialogProps> = ({
  open,
  onClose,
  clockId,
  clockIdf,
}) => {
  const theme = useTheme();

  const defaultFromDate = useMemo(() => getDefaultChangeLogFromDate(), []);

  const [limit, setLimit] = useState<number | "">(100);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [includeDisplayed, setIncludeDisplayed] = useState(false);
  const [pickedKlydoIds, setPickedKlydoIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedKlydoId, setSelectedKlydoId] = useState<string | null>(null);

  const pickedSet = useMemo(() => new Set(pickedKlydoIds), [pickedKlydoIds]);
  const effectiveLimit = typeof limit === "number" ? limit : 100;

  // Open the picker automatically the first time each dialog-open shows with
  // nothing selected — picking the klydos is the entry point of the flow. Gated
  // to once per open so an empty Save (deselect-all) settles into the empty
  // state instead of re-popping the picker.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasAutoOpenedRef.current = false;
      return;
    }
    if (!hasAutoOpenedRef.current && pickedKlydoIds.length === 0) {
      hasAutoOpenedRef.current = true;
      setPickerOpen(true);
    }
  }, [open, pickedKlydoIds.length]);

  const activeFields = useMemo(
    () => PRESENCE_FIELDS.filter((f) => f.key !== "klydo" || includeDisplayed),
    [includeDisplayed],
  );

  const fields = useMemo((): FieldChangeFilter => {
    const filter: FieldChangeFilter = {};
    activeFields.forEach((f) => {
      filter[f.key] = true;
    });
    return filter;
  }, [activeFields]);

  const { data, isLoading, error } = useClockChangeLogs(
    clockId,
    fields,
    pickedKlydoIds.length > 0,
    effectiveLimit,
    fromDate,
  );

  // Raw presence: keep only snapshots where at least one picked klydo is
  // present in at least one active field, with the matched ids per field.
  const rows = useMemo<PresenceRow[]>(() => {
    if (!data || pickedSet.size === 0) return [];
    const result: PresenceRow[] = [];
    data.forEach((row) => {
      const matchesByField: MatchByField[] = activeFields.map((f) => ({
        field: f,
        matched: f.extractIds(row).filter((id) => pickedSet.has(id)),
      }));
      if (matchesByField.some((m) => m.matched.length > 0)) {
        result.push({ row, matchesByField });
      }
    });
    return result;
  }, [data, activeFields, pickedSet]);

  const renderChip = (id: string) => (
    <DSChip
      key={id}
      label={id.length > 8 ? id.slice(0, 8) + "…" : id}
      title={id}
      size="small"
      clickable
      onClick={() => setSelectedKlydoId(id)}
      sx={{
        ...changeLogChipColors(theme, "info"),
        fontSize: "0.75rem",
        height: 24,
        cursor: "pointer",
      }}
    />
  );

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DSDialogTitle>
        <DSBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <DSTypography variant="h6">Klydo Changelog - {clockIdf}</DSTypography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DSBox>
      </DSDialogTitle>

      <DSDialogContent>
        <DSGrid container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <DSGrid item xs={12} md={4}>
            <DSPrimaryButton fullWidth onClick={() => setPickerOpen(true)}>
              {pickedKlydoIds.length > 0
                ? `${pickedKlydoIds.length} klydo${
                    pickedKlydoIds.length > 1 ? "s" : ""
                  } selected — edit`
                : "Pick klydos"}
            </DSPrimaryButton>
          </DSGrid>

          <DSGrid item xs={6} md={2}>
            <DSTextField
              fullWidth
              type="number"
              value={limit}
              onChange={(e) => {
                if (e.target.value === "") {
                  setLimit("");
                  return;
                }
                const n = parseRecordLimit(e.target.value);
                if (n !== null) setLimit(n);
              }}
              helperText="Records to fetch"
            />
          </DSGrid>

          <DSGrid item xs={6} md={2}>
            <DSTextField
              fullWidth
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              helperText="From this date"
            />
          </DSGrid>

          <DSGrid item xs={12} md={4}>
            <DSBox display="flex" alignItems="center">
              <DSSwitch
                checked={includeDisplayed}
                onChange={(e) => setIncludeDisplayed(e.target.checked)}
              />
              <DSTypography variant="caption">
                Include "Displayed" (noisy — rotates often)
              </DSTypography>
            </DSBox>
          </DSGrid>
        </DSGrid>

        {isLoading && (
          <DSBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={5}
          >
            <DSCircularProgress />
            <DSTypography variant="body1" sx={{ mt: 2 }}>
              Loading klydo changelog...
            </DSTypography>
          </DSBox>
        )}

        {error && (
          <DSAlert severity="error" sx={{ mb: 2 }}>
            Error loading data: {error.message}
          </DSAlert>
        )}

        {!isLoading && !error && pickedKlydoIds.length === 0 && (
          <DSBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={5}
          >
            <DSTypography variant="body1" color="textSecondary">
              Pick one or more klydos to see where they appear in this device's
              changelog
            </DSTypography>
          </DSBox>
        )}

        {!isLoading && !error && pickedKlydoIds.length > 0 && (
          <DSTableContainer
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0,
            }}
          >
            <DSTable size="small" sx={{ tableLayout: "auto" }}>
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell
                    sx={{
                      ...headerCellStyles,
                      minWidth: 60,
                      position: "sticky",
                      left: 0,
                      zIndex: 2,
                    }}
                  >
                    Row
                  </DSTableCell>
                  <DSTableCell sx={{ ...headerCellStyles, minWidth: 200 }}>
                    Timestamp
                  </DSTableCell>
                  {activeFields.map((f) => (
                    <DSTableCell
                      key={f.key}
                      sx={{ ...headerCellStyles, minWidth: 160 }}
                    >
                      {f.label}
                    </DSTableCell>
                  ))}
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {rows.length === 0 ? (
                  <DSTableRow>
                    <DSTableCell
                      colSpan={activeFields.length + 2}
                      align="center"
                    >
                      <DSTypography variant="body2" color="textSecondary">
                        No appearances of the selected klydo(s) in the fetched
                        window
                      </DSTypography>
                    </DSTableCell>
                  </DSTableRow>
                ) : (
                  rows.map(({ row, matchesByField }, index) => (
                    <DSTableRow
                      key={`${String(row.timestamp)}-${index}`}
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                        "&:hover": {
                          backgroundColor: theme.palette.action.selected,
                        },
                      }}
                    >
                      <DSTableCell
                        sx={{
                          ...tableCellStyles,
                          position: "sticky",
                          left: 0,
                          backgroundColor: "inherit",
                          zIndex: 1,
                          textAlign: "center",
                        }}
                      >
                        {index + 1}
                      </DSTableCell>
                      <DSTableCell
                        sx={{ ...tableCellStyles, whiteSpace: "nowrap" }}
                      >
                        {row.timestamp ? String(row.timestamp) : "-"}
                      </DSTableCell>
                      {matchesByField.map(({ field, matched }) => (
                        <DSTableCell key={field.key} sx={tableCellStyles}>
                          {matched.length > 0 ? (
                            <DSBox
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {matched.map((id) => renderChip(id))}
                            </DSBox>
                          ) : (
                            "-"
                          )}
                        </DSTableCell>
                      ))}
                    </DSTableRow>
                  ))
                )}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        )}

        {pickerOpen && (
          <KlydosPickDialog
            selectedKlydos={pickedKlydoIds}
            onPickKlydos={setPickedKlydoIds}
            onClose={() => setPickerOpen(false)}
          />
        )}
        {selectedKlydoId && (
          <KlydoPreviewDialog
            klydoId={selectedKlydoId}
            onClose={() => setSelectedKlydoId(null)}
          />
        )}
      </DSDialogContent>
    </DSDialog>
  );
};

export default KlydoChangeLogDialog;
